import { jitsiApi } from "./Jitsi";
import { Logger } from "../Logger.js";
import { getSelectedDeviceMuted } from "../stores.js";
import { ConferencePeer } from "./ConferencePeer";
import { BuildSettings } from "../OutputConfig.js";

import { writable } from 'svelte/store';

export const Conference = {

    ok: false,               // -- conference joined
    peers: new Map(),
    peers_version: writable(0),
    room_name: null,

    // -- Page load. Brings the jitsi connection up and creates the conference object; the
    // -- conference is only *joined* later, from join(), once the Join click has published the
    // -- local tracks onto it.
    async connect(channel_name){

        this.room_name = channel_name;

        try {
            return await jitsiApi.connect(channel_name, this.writeRemoteEvent.bind(this));
        }
        catch (e){
            const info = this._errorInfo(e);
            Logger.log("Conference", "error", "Connect Conference Failed", {error: info});
            return {status: "error", info: info};
        }
    },

    // -- The connection is made at page load but the conference isn't joined until the Join
    // -- click, and the socket can die in between (token/idle timeout, sleep, network change).
    // -- teardownConference nulls the conference on a drop, so re-establish before publishing
    // -- tracks. The capture devices survive that teardown, so the tracks we publish here are the
    // -- same live base tracks as before.
    async ensureConnected(){

        if (jitsiApi.jitsiConference)
            return {status: "ok", info: "ok"};

        if (!this.room_name)
            return {status: "error", info: "No room name (connect was never attempted)"};

        Logger.log("Conference", "warn", "Reconnecting Before Join", {});
        return await this.connect(this.room_name);
    },

    // -- Join click, after addLocalTracks has put the local tracks on the conference.
    async join(){

        try {
            const result = await jitsiApi.join();
            if (result.status === "ok")
                this.ok = true;

            return result;
        }
        catch (e){
            const info = this._errorInfo(e);
            Logger.log("Conference", "error", "Join Conference Failed", {error: info})
            return {status: "error", info: info};
        }
    },

    // -- rejections arrive either as {status, info} objects (our own) or as Errors;
    // -- normalize so the caller can always read .status / .info
    _errorInfo(e){
        return e?.info ?? (e instanceof Error ? e.message : String(e));
    },


    writeRemoteEvent(event){

        const route = event.route;
        const data = event.data;
        const jid = data?.jid;

        if (!data || !jid){
            console.error("PL-Conference] Write Remote Event malformed event: ", event);
            return;
        }

        let cp = this.peers.get(jid);
        let changed = false;

        if (!cp) {
            cp = new ConferencePeer(jid);
            this.peers.set(jid, cp);
            changed = true;
        }

        switch  (route){

            case "user-join":
                // -- fired by USER_JOINED and again by the PARTICIPANT_PROPERTY_CHANGED backstop;
                // -- setUser reports when the pl_id property newly landed
                if (cp.setUser(data.user)){
                    this._evictStalePeers(cp);
                    Logger.log("Conference", "info", "Participant Bound", {jid: jid, pl_id: cp.pl_id});
                    changed = true;
                }
                break;
            case "add-track":
                cp.setRemoteTrack(data.track);
                break;
            case "remove-track":
                cp.removeRemoteTrack(data.track);
                break;
            case "track-mute":
                cp.setRemoteMute(data.track);
                break;
        }

        // -- bumped *after* the event applied, so a tile binding off this pass already sees what
        // -- it was bumped for. Track updates ride the per-peer stores and need no bump.
        if (changed)
            this.peers_version.update(v => v + 1);
    },

    // -- tiles bind by pl-ws id: returns the ConferencePeer carrying that participant's tracks,
    // -- or null until its join presence (with the pl_id property) has arrived
    findByPlId(pl_id){
        if (!pl_id) return null;
        for (const cp of this.peers.values())
            if (cp.pl_id === pl_id) return cp;
        return null;
    },

    // -- a rejoining participant returns with a fresh jid but the same pl_id; this map is never
    // -- pruned (USER_LEFT is deliberately ignored), so the dead session's entry would otherwise
    // -- shadow the new one in findByPlId forever
    _evictStalePeers(cp){
        for (const [jid, other] of this.peers){
            if (other !== cp && other.pl_id === cp.pl_id)
                this.peers.delete(jid);
        }
    },

    async addLocalTracks(request, stream){

        try {

            // -- gated on the conference *object*, not on having joined: tracks are published
            // -- pre-join now (that's the point), so the only precondition is that the conference
            // -- exists to receive them
            if (!jitsiApi.jitsiConference || !stream) return false;

            const jitsi_track_info = [];
            const streamMode = BuildSettings.CONFERENCE_STREAM_MODE;
            const clonedStream = streamMode === "clone-stream" ? stream.clone() : null;
            
            for (const type in request){
                const track = request[type].track;
                let conferenceTrack = track;
                let conferenceStream = stream;

                if (streamMode === "clone-stream") {
                    conferenceStream = clonedStream;
                    conferenceTrack = clonedStream?.getTracks().find(t => t.kind === type);
                } else if (streamMode === "isolated-track-streams") {
                    conferenceStream = new MediaStream([track]);
                }

                if (track && conferenceTrack){

                    // -- forensics: a base track that died (the camera revoked, the OS handing
                    // -- the device to another app, a stop() we didn't intend) publishes
                    // -- "successfully" but is silent/frozen for every remote -- make that
                    // -- visible in the remote logs
                    if (track.readyState === "ended" || conferenceTrack.readyState === "ended")
                        Logger.log("Conference", "error", "Publishing Ended Track", {kind: type});

                    jitsi_track_info.push({
                        mediaType: type,
                        sourceType: type === "audio" ? "mic" : "camera",
                        stream: conferenceStream,
                        track: conferenceTrack,
                        ...(type === "video" ? { videoType: "camera" } : {})
                    });
                }
            }

            const jitsi_tracks = jitsiApi.createLocalTracksFromMediaStreams(jitsi_track_info);

            for (const jt of jitsi_tracks) {
                const type = jt.getType();
                // -- contained on purpose so one kind failing doesn't take the other down with it;
                // -- _addOrReplaceTrack has already pushed the failure to the remote logs
                await this._addOrReplaceTrack(jt).catch(
                    e => console.error(e)
                );

                // -- a freshly wrapped track publishes unmuted; the selection's muted store is
                // -- the source of truth (e.g. the user muted the mic, then Join published, or
                // -- switched devices while muted), so make the jitsi track observe it
                const device_type = type === "audio" ? "microphone" : "camera";
                if (getSelectedDeviceMuted(device_type) && !jt.isMuted())
                    await jt.mute().catch(e => console.error(e));

                console.log("[PL-Conference] Adding New Local Conference Track: ", type);
            }

            return true;

        } catch (e){
            // -- we should just throw this or something
            console.error(e)
            return false;
        }

    },


    // -- Remove the local track of a given media type ("audio"/"video") from the conference,
    // -- e.g. when its device permission is revoked. Disposing the jitsi track detaches it from
    // -- the conference and stops the (possibly cloned) underlying track it owns.
    async removeLocalTrack(type){

        try {
            const t = jitsiApi.jitsiConference?.getLocalTracks(type)?.[0];
            if (!t) return false;

            await t.dispose();
            console.log("[PL-Conference] Removed Local Conference Track: ", type);
            return true;

        } catch (e){
            console.error(e);
            return false;
        }
    },


    // -- apply a desired mute state to our local conference track of this media type
    // -- ("audio"/"video"). The selected_devices muted store is the source of truth; this makes
    // -- the live jitsi track follow it. Returns false when we don't publish this kind.
    async applyLocalMute(type, muted){

        try {

            const t = jitsiApi.jitsiConference?.getLocalTracks(type)?.[0];
            if (!t) return false;

            if (t.isMuted() !== muted)
                muted ? await t.mute() : await t.unmute();

            console.log("[PL-Conference] Local Track Mute Applied: ", type, t.isMuted());
            return true;

        } catch(e) {
            console.error(e);
            return false;
        }

    },

    // -- the MediaStreamTrack currently under our local jitsi track of this type; lib-jitsi-meet
    // -- can swap it (a camera unmute re-acquires the device), so Devices re-reads it from here
    getLocalMediaStreamTrack(type){
        return jitsiApi.jitsiConference?.getLocalTracks(type)?.[0]?.getTrack?.() ?? null;
    },

    sendChatMessage(msg){
        
        try {
            jitsiApi.jitsiConference.sendMessage({to: 'all', from: jitsiApi.jitsiConference.myUserId(), payload: msg});
        }
        catch (e) {
            jitsiApi.jitsiConference.sendTextMessage(JSON.stringify({to: 'all', from: jitsiApi.jitsiConference.myUserId(), payload: msg}));
        }
    },

    cleanUp(){
        jitsiApi.cleanupLocal();
    },

    // -- do we currently publish this media type ("audio"/"video") on the *live* conference? Not
    // -- the same as "we once published it": a reconnect builds a fresh conference object with no
    // -- tracks on it while the device selection (label/id) stays put.
    hasLocalTrack(type){
        return (jitsiApi.jitsiConference?.getLocalTracks(type)?.length ?? 0) > 0;
    },

    // -- replaceTrack swaps a live sender in place (no renegotiation) and is only valid when we
    // -- already publish this kind -- i.e. a device switch. Everything else is a genuine add:
    // -- the pre-join publish, and unmuting a camera that was closed rather than jitsi-muted.
    async _addOrReplaceTrack(nt){

        const type = nt.getType();
        const ot = jitsiApi.jitsiConference?.getLocalTracks(type)?.[0];
        const op = ot ? "replace" : "add";

        try {
            if (ot) await jitsiApi.jitsiConference?.replaceTrack(ot, nt);
            else     await jitsiApi.jitsiConference?.addTrack(nt);
        }
        catch (e){
            // -- a failure here can leave the sender publishing an ended/stale track: this peer
            // -- goes silent (or frozen) for everyone while their own UI looks fine -- exactly the
            // -- "tile can't be heard" shape -- so it must reach the remote logs, not just console
            Logger.log("Conference", "error", "Publish Track Failed", {kind: type, op: op, joined: this.ok, error: e?.message ?? String(e)});
            throw e;
        }
    },

}
